/**
 * Comprehensive Inspection Reports Analytics Dashboard
 * 
 * Features:
 * - Advanced filtering system
 * - Interactive charts and visualizations
 * - Real-time data analysis
 * - Export capabilities
 * - Performance metrics and KPIs
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Autocomplete,
    Stack,
    Divider,
    Paper
} from '@mui/material';
import {
    FilterList as FilterIcon,
    Assessment as AssessmentIcon,
    LocationCity as LocationCityIcon,
    Business as BusinessIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Map as MapIcon
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart
} from 'recharts';
import { DataGrid } from '@mui/x-data-grid';
import MunicipalityMap from './MunicipalityMap';

// API configuration - checks REACT_APP_API_BASE first, then REACT_APP_API_URL
const getApiBase = () => {
    if (process.env.REACT_APP_API_BASE) {
        // REACT_APP_API_BASE already includes /api, so remove it if present
        return process.env.REACT_APP_API_BASE.replace(/\/api$/, '');
    }
    return process.env.REACT_APP_API_URL || 'http://localhost:8010';
};

// Static data loader for deployments without backend
const loadStaticData = async () => {
    // Try Cloudflare Worker first (proxies R2 with CORS headers)
    const r2Urls = [
        'https://inspection-dashboard-data-proxy.alexanderleenaers.workers.dev',
        '/dashboard-data.json' // Fallback to local file
    ];

    for (const url of r2Urls) {
        try {
            console.log(`🔄 Attempting to load data from: ${url}`);
            const response = await fetch(url);
            console.log(`📡 Response status: ${response.status} ${response.statusText}`);

            if (response.ok) {
                console.log(`✅ Loading JSON data from: ${url}`);
                const data = await response.json();
                console.log(`✅ Data loaded successfully! Reports: ${data.reports?.length || 0}`);
                return data;
            } else {
                console.warn(`⚠️ HTTP ${response.status} from ${url}`);
            }
        } catch (error) {
            console.error(`❌ Error loading from ${url}:`, error.message);
        }
    }

    // Static file not found, will use API instead
    console.error('❌ All static data sources failed');
    return null;
};

// Dashboard API service
class DashboardAPI {
    static async get(endpoint, filters = {}) {
        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/api/inspection-dashboard${endpoint}`, {
            method: endpoint.includes('/analytics') || endpoint.includes('/reports') ? 'POST' : 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            body: endpoint.includes('/analytics') || endpoint.includes('/reports') ? JSON.stringify(filters) : undefined
        });

        if (!response.ok) {
            throw new Error(`API Error: ${response.statusText}`);
        }

        return response.json();
    }

    static async getOverview() {
        return this.get('/overview');
    }

    static async getFilterOptions() {
        return this.get('/filters/options');
    }

    static async getReports(filters) {
        return this.get('/reports', filters);
    }

    static async getTemporalAnalytics(filters) {
        return this.get('/analytics/temporal', filters);
    }

    static async getMunicipalityAnalytics(filters) {
        return this.get('/analytics/municipality', filters);
    }

    static async getViolationAnalytics(filters) {
        return this.get('/analytics/violations', filters);
    }

    static async getQualityMetrics(filters) {
        return this.get('/analytics/quality-metrics', filters);
    }

    static async exportData(filters, format = 'json') {
        return this.get('/export/data', { filters, format });
    }

    // New: export full dashboard snapshot (filtered and normalized)
    static async exportDashboard(filters) {
        const apiBase = getApiBase();
        const response = await fetch(`${apiBase}/api/inspection-dashboard/export/dashboard`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(filters || {})
        });
        if (!response.ok) {
            throw new Error(`API Error: ${response.statusText}`);
        }
        return response.json();
    }
}

// Color schemes for charts
const CHART_COLORS = {
    quality: {
        approved: '#4caf50',
        partially_approved: '#ff9800',
        rejected: '#f44336',
        unknown_status: '#9e9e9e'
    },
    trend: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'],
    violation: ['#f44336', '#ff5722', '#ff9800', '#ffc107', '#ffeb3b']
};

// Filter Panel Component
const FilterPanel = ({ filters, onFiltersChange, filterOptions, loading }) => {
    const handleFilterChange = useCallback((key, value) => {
        onFiltersChange({
            ...filters,
            [key]: value
        });
    }, [filters, onFiltersChange]);

    const clearFilters = useCallback(() => {
        onFiltersChange({ years: [2024, 2025] });
    }, [onFiltersChange]);

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FilterIcon sx={{ mr: 1 }} />
                        Filters
                    </Typography>
                    <Button
                        size="small"
                        onClick={clearFilters}
                        disabled={loading}
                    >
                        Alles Wissen
                    </Button>
                </Box>

                <Grid container spacing={2}>
                    {/* Years Filter */}
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Jaren</InputLabel>
                            <Select
                                multiple
                                value={filters.years || [2024, 2025]}
                                onChange={(e) => handleFilterChange('years', e.target.value)}
                                label="Jaren"
                                disabled={loading}
                                MenuProps={{
                                    disableScrollLock: true
                                }}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} label={value} size="small" />
                                        ))}
                                    </Box>
                                )}
                            >
                                {[2024, 2025].map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Municipalities Filter */}
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={filterOptions.municipalities || []}
                            value={filters.municipalities || []}
                            onChange={(event, newValue) => {
                                handleFilterChange('municipalities', newValue);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Gemeenten" />
                            )}
                            disabled={loading}
                            disableScrollLock={true}
                        />
                    </Grid>
                </Grid>

                {/* Active Filters Display */}
                {Object.keys(filters).length > 0 && (
                    <Box mt={2}>
                        <Typography variant="caption" color="text.secondary">
                            Actieve Filters:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {Object.entries(filters).map(([key, value]) => {
                                if (!value || (Array.isArray(value) && value.length === 0)) return null;

                                const displayValue = Array.isArray(value)
                                    ? `${value.length} geselecteerd`
                                    : value.toString();

                                return (
                                    <Chip
                                        key={key}
                                        label={`${key.replace('_', ' ')}: ${displayValue}`}
                                        size="small"
                                        onDelete={() => handleFilterChange(key, Array.isArray(value) ? [] : null)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

// KPI Cards Component
const KPICards = ({ stats, loading }) => {
    const kpis = [
        {
            title: 'Totaal Voorzieningen',
            value: stats?.total_facilities?.toLocaleString() || '0',
            icon: <BusinessIcon />,
            color: 'primary'
        },
        {
            title: 'Inspectierapporten',
            value: stats?.total_reports?.toLocaleString() || '0',
            icon: <AssessmentIcon />,
            color: 'secondary'
        },
        {
            title: 'Gemeenten',
            value: stats?.total_municipalities?.toLocaleString() || '0',
            icon: <LocationCityIcon />,
            color: 'info'
        },
        {
            title: 'Naleving Percentage',
            value: `${((stats?.compliance_rate ?? stats?.approval_rate) ?? 0)}%`,
            icon: (((stats?.compliance_rate ?? stats?.approval_rate) ?? 0) >= 80) ? <CheckCircleIcon /> : <WarningIcon />,
            color: (((stats?.compliance_rate ?? stats?.approval_rate) ?? 0) >= 80) ? 'success' : 'warning'
        }
    ];

    return (
        <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpis.map((kpi, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="text.secondary" gutterBottom variant="body2">
                                        {kpi.title}
                                    </Typography>
                                    <Typography variant="h4" component="div">
                                        {loading ? <CircularProgress size={24} /> : kpi.value}
                                    </Typography>
                                </Box>
                                <Box color={`${kpi.color}.main`}>
                                    {kpi.icon}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

// Charts Components
const QualityDistributionChart = ({ data, loading }) => {
    const categoryTranslations = {
        'no_violations': 'Geen overtredingen',
        'with_violations': 'Met overtredingen'
    };

    const chartData = useMemo(() => {
        if (!data) return [];

        const entries = Object.entries(data).map(([category, count]) => ({
            name: categoryTranslations[category] || category.replace('_', ' ').toUpperCase(),
            value: count,
            category
        }));
        const total = entries.reduce((sum, e) => sum + e.value, 0);
        return entries.map(e => ({ ...e, percent: total ? ((e.value / total) * 100) : 0 }));
    }, [data]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={300}>
            <PieChart>
                <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                >
                    {chartData.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS.quality[entry.category] || CHART_COLORS.trend[index % CHART_COLORS.trend.length]}
                        />
                    ))}
                </Pie>
                <RechartsTooltip
                    formatter={(value, name, props) => {
                        const pct = props && props.payload ? props.payload.percent : 0;
                        return [`${value} (${pct.toFixed(1)}%)`, name];
                    }}
                />
                <Legend formatter={(value) => {
                    const item = chartData.find(e => e.name === value);
                    if (!item) return value;
                    return `${value} (${item.percent.toFixed(1)}%)`;
                }} />
            </PieChart>
        </ResponsiveContainer>
    );
};

const TemporalTrendsChart = ({ data, loading }) => {
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data?.monthly_trends || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    tickFormatter={(value) => {
                        const date = new Date(value + '-01');
                        return date.toLocaleDateString('nl-NL', { month: 'short', year: '2-digit' });
                    }}
                />
                <YAxis />
                <RechartsTooltip
                    labelFormatter={(value) => {
                        const date = new Date(value + '-01');
                        return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
                    }}
                />
                <Legend />
                <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2196f3"
                    strokeWidth={2}
                    name="Inspectierapporten"
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

const MunicipalityComparisonChart = ({ data, loading, sortKey }) => {

    const chartData = useMemo(() => {
        if (!data?.municipality_stats) return [];

        const computed = data.municipality_stats.map(municipality => {
            const compliance_rate = municipality.compliance_rate != null
                ? municipality.compliance_rate
                : municipality.reports_with_violations != null
                    ? ((municipality.total_reports - municipality.reports_with_violations) / Math.max(municipality.total_reports, 1)) * 100
                    : municipality.quality_distribution
                        ? ((municipality.quality_distribution.approved + municipality.quality_distribution.partially_approved) /
                            Math.max(municipality.total_reports, 1)) * 100
                        : 0;
            return {
                ...municipality,
                approval_rate: compliance_rate
            };
        });

        const sorted = [...computed].sort((a, b) => {
            if (sortKey === 'rate') return b.approval_rate - a.approval_rate;
            return b.total_reports - a.total_reports;
        });

        return sorted.slice(0, 50); // show up to 50
    }, [data, sortKey]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={400}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={chartData} margin={{ left: 20, right: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="municipality"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                    interval={0}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[0, 100]} />
                <RechartsTooltip formatter={(v, name) => name === 'Goedkeuringspercentage (%)' ? `${v.toFixed(1)}%` : v} />
                <Legend />
                <Bar yAxisId="left" dataKey="total_reports" fill="#2196f3" name="Totaal Rapporten" />
                <Line yAxisId="right" type="monotone" dataKey="approval_rate" stroke="#4caf50" strokeWidth={2} name="Goedkeuringspercentage (%)" />
            </ComposedChart>
        </ResponsiveContainer>
    );
};


const ReportsTable = ({ reports, loading, pagination, onPageChange }) => {
    const columns = [
        { field: 'facility_name', headerName: 'Voorziening', width: 200 },
        { field: 'municipality', headerName: 'Gemeente', width: 150 },
        { field: 'facility_type', headerName: 'Type', width: 100 },
        {
            field: 'inspection_date',
            headerName: 'Datum',
            width: 120,
            valueFormatter: (params) => {
                return params.value ? new Date(params.value).toLocaleDateString('nl-NL') : '';
            }
        },
        {
            field: 'quality',
            headerName: 'Kwaliteit',
            width: 150,
            renderCell: (params) => {
                const quality = params.value;
                if (!quality) return null;

                const colors = {
                    approved: 'success',
                    partially_approved: 'warning',
                    rejected: 'error',
                    unknown_status: 'default'
                };

                return (
                    <Chip
                        label={quality.category.replace('_', ' ').toUpperCase()}
                        color={colors[quality.category]}
                        size="small"
                    />
                );
            }
        },
        {
            field: 'quality_score',
            headerName: 'Score',
            width: 100,
            valueGetter: (params) => params.row.quality?.quality_score || 0,
            valueFormatter: (params) => params.value.toFixed(1)
        },
        {
            field: 'violations_count',
            headerName: 'Overtredingen',
            width: 100,
            valueGetter: (params) => params.row.quality?.violations_count || 0
        },
        {
            field: 'pdf_url',
            headerName: 'Rapport',
            width: 100,
            renderCell: (params) => (
                <Button
                    size="small"
                    onClick={() => window.open(params.value, '_blank')}
                    disabled={!params.value}
                >
                    Bekijk PDF
                </Button>
            )
        }
    ];

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Inspectierapporten
                </Typography>
                <Box height={600}>
                    <DataGrid
                        rows={reports || []}
                        columns={columns}
                        loading={loading}
                        paginationMode="server"
                        rowCount={pagination?.total || 0}
                        page={pagination?.page - 1 || 0}
                        pageSize={pagination?.per_page || 50}
                        rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(page) => onPageChange({ page: page + 1 })}
                        onPageSizeChange={(pageSize) => onPageChange({ per_page: pageSize })}
                        disableSelectionOnClick
                        checkboxSelection
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

// Main Dashboard Component
const InspectionReportsDashboard = () => {
    // State management
    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({ years: [2024, 2025], municipalities: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Static mode
    const [staticData, setStaticData] = useState(null);
    const [useStaticMode, setUseStaticMode] = useState(false);

    // Data state
    const [overview, setOverview] = useState(null);
    const [filterOptions, setFilterOptions] = useState({});
    const [reports, setReports] = useState([]);
    const [reportsPagination, setReportsPagination] = useState({});
    const [temporalData, setTemporalData] = useState(null);
    const [municipalityData, setMunicipalityData] = useState(null);
    const [violationData, setViolationData] = useState(null);
    const [qualityMetrics, setQualityMetrics] = useState(null);

    // Combined state for KPI cards
    const [kpiStats, setKpiStats] = useState(null);

    // Sort control for municipality comparison
    const [municipalitySort, setMunicipalitySort] = useState('reports');

    // Check for static data on mount
    useEffect(() => {
        const checkStaticData = async () => {
            setLoading(true);
            const data = await loadStaticData();
            if (data) {
                console.log('📊 Using static data mode');
                console.log('🗺️  Map facilities loaded:', data.map_facilities?.length || 0);
                setStaticData(data);
                setUseStaticMode(true);
                setFilterOptions({
                    municipalities: data.filter_options.municipalities,
                    years: data.filter_options.years
                });
                setLoading(false);
            } else {
                // Only use API mode if we're on localhost
                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                    console.log('🌐 Using API mode (localhost detected)');
                    loadInitialData();
                } else {
                    console.error('❌ Static data not available and not on localhost');
                    setError('Data kon niet worden geladen. Probeer later opnieuw.');
                    setLoading(false);
                }
            }
        };
        checkStaticData();
    }, []);

    // Load data when filters change (top 10 lists should work without municipality selection)
    useEffect(() => {
        if (Object.keys(filterOptions).length > 0) {
            if (useStaticMode) {
                processStaticData();
            } else {
                loadDashboardData();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, filterOptions, useStaticMode]);

    const loadInitialData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [overviewResult, optionsResult] = await Promise.all([
                DashboardAPI.getOverview(),
                DashboardAPI.getFilterOptions()
            ]);

            if (overviewResult.success) {
                setOverview(overviewResult.data);
                setKpiStats(overviewResult.data); // Set initial KPI stats
            }

            if (optionsResult.success) {
                setFilterOptions(optionsResult.data);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const processStaticData = () => {
        if (!staticData) return;

        setLoading(true);

        try {
            // Filter reports based on selected filters
            let filteredReports = staticData.reports.filter(report => {
                // Municipality filter
                if (filters.municipalities && filters.municipalities.length > 0) {
                    if (!filters.municipalities.includes(report.municipality)) return false;
                }

                // Year filter
                if (filters.years && filters.years.length > 0) {
                    if (!filters.years.includes(report.year)) return false;
                }

                return true;
            });

            // Format reports for display
            const formattedReports = filteredReports.map(report => ({
                id: report.id,
                facility_name: report.facility_name,
                facility_type: report.facility_type,
                municipality: report.municipality,
                lrk_id: report.lrk_id,
                inspection_date: report.inspection_date,
                label: report.label,
                pdf_url: report.pdf_url,
                quality: {
                    category: report.conclusion_category,
                    conclusion: report.conclusion,
                    strengths_count: report.strengths_count,
                    violations_count: report.violations_count,
                    improvements_count: report.improvements_count,
                    quality_score: report.quality_score
                }
            }));

            setReports(formattedReports);
            setReportsPagination({ page: 1, per_page: 50, total: formattedReports.length, pages: Math.ceil(formattedReports.length / 50) });

            const totalReports = filteredReports.length;
            // Calculate compliance metrics from filtered data (based on violations)
            let reportsWithViolations = 0;
            filteredReports.forEach(r => {
                if ((r.violations_count || 0) > 0) {
                    reportsWithViolations += 1;
                }
            });
            const reportsWithoutViolations = totalReports - reportsWithViolations;
            const complianceDist = {
                no_violations: reportsWithoutViolations,
                with_violations: reportsWithViolations
            };
            const complianceRate = totalReports > 0 ? ((reportsWithoutViolations / totalReports) * 100) : 0;

            // Calculate aggregations from filtered reports
            const improvementsCounts = {};
            const adviceCounts = {};
            const violationsCounts = {};

            filteredReports.forEach(r => {
                // Aggregate improvements
                if (r.improvements && Array.isArray(r.improvements)) {
                    r.improvements.forEach(improvement => {
                        if (improvement && typeof improvement === 'string' && improvement.trim()) {
                            improvementsCounts[improvement] = (improvementsCounts[improvement] || 0) + 1;
                        }
                    });
                }

                // Aggregate advice
                if (r.advice_to_municipality && typeof r.advice_to_municipality === 'string' && r.advice_to_municipality.trim()) {
                    adviceCounts[r.advice_to_municipality] = (adviceCounts[r.advice_to_municipality] || 0) + 1;
                }

                // Aggregate violations
                if (r.violations && Array.isArray(r.violations)) {
                    r.violations.forEach(violation => {
                        if (violation && typeof violation === 'string' && violation.trim()) {
                            violationsCounts[violation] = (violationsCounts[violation] || 0) + 1;
                        }
                    });
                }
            });

            // Sort and take top 10
            let top_improvements = Object.fromEntries(
                Object.entries(improvementsCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)
            );
            const advice_distribution = Object.fromEntries(
                Object.entries(adviceCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)
            );
            let top_violations = Object.fromEntries(
                Object.entries(violationsCounts).sort((a, b) => b[1] - a[1]).slice(0, 10)
            );

            // In static mode, use precomputed aggregations from export (arrays are not included)
            if (staticData?.aggregations?.top_improvements) {
                top_improvements = staticData.aggregations.top_improvements;
            }
            if (staticData?.aggregations?.top_violations) {
                top_violations = staticData.aggregations.top_violations;
            }

            setQualityMetrics({
                total_reports: totalReports,
                compliance_distribution: complianceDist,
                compliance_rate: complianceRate.toFixed(2),
                reports_with_violations: reportsWithViolations,
                top_improvements: top_improvements,
                advice_distribution: advice_distribution
            });

            const uniqueMunicipalities = new Set(
                filteredReports
                    .map(r => r.municipality)
                    .filter(m => m && m !== 'Onbekend')
            );

            setKpiStats({
                total_facilities: new Set(filteredReports.map(r => r.facility_name)).size,
                total_reports: totalReports,
                total_municipalities: uniqueMunicipalities.size,
                compliance_rate: complianceRate.toFixed(2)
            });

            // Temporal trends
            const monthlyTrends = {};
            filteredReports.forEach(r => {
                if (r.inspection_date) {
                    const monthKey = r.inspection_date.substring(0, 7); // YYYY-MM
                    monthlyTrends[monthKey] = (monthlyTrends[monthKey] || 0) + 1;
                }
            });

            setTemporalData({
                monthly_trends: Object.entries(monthlyTrends).map(([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date))
            });

            // Violation data
            setViolationData({
                top_violations: top_violations
            });

            // Municipality comparison data - match API structure
            const municipalityStats = {};
            filteredReports.forEach(r => {
                const mun = r.municipality;
                if (!mun) return; // Skip if no municipality

                if (!municipalityStats[mun]) {
                    municipalityStats[mun] = {
                        municipality: mun,
                        total_reports: 0,
                        reports_with_violations: 0,
                        total_violations: 0,
                        total_improvements: 0,
                        quality_scores: []
                    };
                }

                municipalityStats[mun].total_reports += 1;

                if ((r.violations_count || 0) > 0) {
                    municipalityStats[mun].reports_with_violations += 1;
                }

                municipalityStats[mun].total_violations += r.violations_count || 0;
                municipalityStats[mun].total_improvements += r.improvements_count || 0;
                municipalityStats[mun].quality_scores.push(r.quality_score || 0);
            });

            // Calculate averages
            const municipalityStatsArray = Object.values(municipalityStats).map(stats => {
                const avgQualityScore = stats.quality_scores.length > 0
                    ? stats.quality_scores.reduce((a, b) => a + b, 0) / stats.quality_scores.length
                    : 0;

                const compliance_rate = stats.total_reports > 0
                    ? ((stats.total_reports - stats.reports_with_violations) / stats.total_reports) * 100
                    : 0;
                return {
                    municipality: stats.municipality,
                    total_reports: stats.total_reports,
                    reports_with_violations: stats.reports_with_violations,
                    compliance_rate,
                    total_violations: stats.total_violations,
                    total_improvements: stats.total_improvements,
                    avg_quality_score: avgQualityScore
                };
            });

            setMunicipalityData({
                municipality_stats: municipalityStatsArray
            });

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadDashboardData = async () => {
        setLoading(true);
        setError(null);

        try {
            const promises = [
                DashboardAPI.getReports({ ...filters, page: 1, per_page: 50 }),
                DashboardAPI.getTemporalAnalytics(filters),
                DashboardAPI.getMunicipalityAnalytics(filters),
                DashboardAPI.getViolationAnalytics(filters),
                DashboardAPI.getQualityMetrics(filters)
            ];

            const [reportsResult, temporalResult, municipalityResult, violationResult, qualityResult] =
                await Promise.all(promises);

            if (reportsResult.success) {
                setReports(reportsResult.data.reports);
                setReportsPagination(reportsResult.data.pagination);
            }

            if (temporalResult.success) {
                setTemporalData(temporalResult.data);
            }

            if (municipalityResult.success) {
                setMunicipalityData(municipalityResult.data);
            }

            if (violationResult.success) {
                setViolationData(violationResult.data);
            }

            if (qualityResult.success) {
                setQualityMetrics(qualityResult.data);
                setKpiStats(qualityResult.data); // Update KPI stats with filtered data
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const refreshData = useCallback(() => {
        loadInitialData();
        if (Object.keys(filterOptions).length > 0) {
            loadDashboardData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterOptions]);

    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
    }, []);

    const handleReportPageChange = useCallback(async (pagination) => {
        setLoading(true);
        try {
            const result = await DashboardAPI.getReports({
                ...filters,
                ...pagination
            });

            if (result.success) {
                setReports(result.data.reports);
                setReportsPagination(result.data.pagination);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Check if municipality is selected
    const hasMunicipalitySelected = filters.municipalities && filters.municipalities.length > 0;

    // Tab panels
    const tabPanels = [
        {
            label: 'Kaart',
            icon: <MapIcon />,
            content: (
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Overtredingen per Gemeente - Interactieve Kaart
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Deze kaart toont het gemiddeld aantal overtredingen per voorziening per gemeente voor ALLE data.
                        De kaart wordt niet beïnvloed door de filters. Klik op een gemeente voor meer details.
                    </Typography>
                    <MunicipalityMap
                        loading={loading}
                        staticFacilities={staticData?.map_facilities || []}
                        staticMunicipalityStats={staticData?.municipality_stats || []}
                    />
                </Box>
            )
        },
        {
            label: 'Overzicht',
            content: !hasMunicipalitySelected ? (
                <Card>
                    <CardContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="400px"
                            textAlign="center"
                        >
                            <LocationCityIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h5" gutterBottom>
                                Selecteer een Gemeente
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Kies één of meerdere gemeenten uit het filter hierboven om het analytics dashboard te bekijken.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {/* --- ROW 1: High-Level Overview --- */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Verdeling Rapporten (naleving)
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                    Op basis van overtredingen in inspectierapporten
                                </Typography>
                                <QualityDistributionChart
                                    data={qualityMetrics?.compliance_distribution || qualityMetrics?.quality_distribution}
                                    loading={loading}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Inspectie Trends
                                </Typography>
                                <TemporalTrendsChart
                                    data={temporalData}
                                    loading={loading}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* --- ROW 2: Municipality Analysis --- */}
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Gemeente vergelijking
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            selecteer meerdere gemeentes om te vergelijken
                                        </Typography>
                                    </Box>
                                    <FormControl size="small">
                                        <InputLabel id="sort-by-label">Sorteren op</InputLabel>
                                        <Select
                                            labelId="sort-by-label"
                                            label="Sorteren op"
                                            value={municipalitySort}
                                            onChange={(e) => setMunicipalitySort(e.target.value)}
                                            sx={{ minWidth: 160 }}
                                            MenuProps={{
                                                disableScrollLock: true
                                            }}
                                        >
                                            <MenuItem value="reports">Totaal Rapporten</MenuItem>
                                            <MenuItem value="rate">Goedkeuringspercentage</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <MunicipalityComparisonChart
                                    data={municipalityData}
                                    loading={loading}
                                    sortKey={municipalitySort}
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* --- ROW 3: Violations Analysis --- */}
                    <Grid item xs={12} md={12}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Top 10 Overtredingen
                                </Typography>
                                {loading ? <CircularProgress /> : (
                                    violationData?.top_violations && (
                                        <Stack spacing={1} pr={1}>
                                            {Object.entries(violationData.top_violations)
                                                .slice(0, 10)
                                                .map(([text, count]) => (
                                                    <Box key={text} display="flex" justifyContent="space-between">
                                                        <Typography variant="body2" sx={{ flex: 1, mr: 1 }} title={text}>
                                                            {String(text)}
                                                        </Typography>
                                                        <Chip size="small" label={count} />
                                                    </Box>
                                                ))}
                                        </Stack>
                                    )
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* --- ROW 4 & 5: Qualitative Insights & KPIs --- */}
                    <Grid item xs={12} md={12}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Top 10 Verbeterpunten
                                </Typography>
                                {loading ? <CircularProgress /> : (
                                    qualityMetrics?.top_improvements && (
                                        <Stack spacing={1} pr={1}>
                                            {Object.entries(qualityMetrics.top_improvements)
                                                .slice(0, 10)
                                                .map(([text, count]) => (
                                                    <Box key={text} display="flex" justifyContent="space-between">
                                                        <Typography variant="body2" sx={{ flex: 1, mr: 1 }} title={text}>
                                                            {String(text)}
                                                        </Typography>
                                                        <Chip size="small" label={count} />
                                                    </Box>
                                                ))}
                                        </Stack>
                                    )
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )
        },
        {
            label: 'Rapporten',
            content: !hasMunicipalitySelected ? (
                <Card>
                    <CardContent>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="400px"
                            textAlign="center"
                        >
                            <AssessmentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h5" gutterBottom>
                                Selecteer een Gemeente
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Kies één of meerdere gemeenten uit het filter hierboven om inspectierapporten te bekijken.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <ReportsTable
                    reports={reports}
                    loading={loading}
                    pagination={reportsPagination}
                    onPageChange={handleReportPageChange}
                />
            )
        }
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Inspectierapporten Analytics Dashboard
                </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Filter Panel */}
            <FilterPanel
                filters={filters}
                onFiltersChange={handleFiltersChange}
                filterOptions={filterOptions}
                loading={loading}
            />

            {/* KPI Cards */}
            <KPICards
                stats={kpiStats || overview}
                loading={loading}
            />

            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    {tabPanels.map((panel, index) => (
                        <Tab
                            key={index}
                            label={panel.label}
                            icon={panel.icon}
                            iconPosition="start"
                        />
                    ))}
                </Tabs>
            </Box>

            {/* Tab Content */}
            {tabPanels[activeTab]?.content}

            {/* Loading Overlay */}
            {loading && (
                <Box
                    position="fixed"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgcolor="rgba(0,0,0,0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={9999}
                >
                    <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress />
                        <Typography>Dashboard gegevens laden...</Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default InspectionReportsDashboard;